/* ═════════════════════ tasks, timers, and the multiple ═════════════════════

   The part of the app that does the measuring. A task carries an estimate, a
   pausable timer, and — once it is finished — the ratio between the two, which
   is what every prediction anywhere else in studyHub is ultimately built from.

   The panel, the list, the session view and the pomodoro all live here because
   they are all the same object seen from different distances. */
import { choiceAttrs, elapsedSec, estBaseline, quantityAttr, wasStarted } from '../core/domain.js';
import { deadlineById, deadlineLoad, standingMultiple } from '../state/evidence.js';
import { COURSE_COLORS } from '../core/palette.js';
import { DAY_MS, clamp, dayStart, escapeHtml, fmtClock, fmtDurMin, fmtWhen, fromLocalInput, startOfToday, toLocalInput, uid } from '../core/time.js';
import { touchPlan } from '../state/plans.js';
import { S, courseById, courseColor, courseName, save } from '../state/store.js';
import { clearPendingPromotion, noteById, pendingPromotion, renderNote, updateThoughtChrome } from './board.js';
import { openInGoogle, renderCalendar, renderScheduled } from './calendar.js';
import { renderClock } from './clock.js';
import { askText, closeModal, el, openModal, setDrawer, toast } from './dom.js';
import { renderLtGroupFilter } from './longterm.js';
import { refreshPlanningTabs } from './shell.js';
import { needsIntake, openIntake } from './series.js';
import { renderWkGroupFilter } from './weekly.js';
import { $ } from './dom.js';
import { fmtHours } from '../core/time.js';

export function multClass(m) { return m <= 1.1 ? 'is-good' : m <= 1.75 ? 'is-warn' : 'is-bad'; }

export function renderStanding() {
  const m = standingMultiple();
  const v = $('standingValue');
  const n = S.tasks.filter((t) => t.done && typeof t.multiple === 'number').length;
  v.className = 'pill-value' + (m ? ' ' + multClass(m) : '');
  v.textContent = m ? `${m.toFixed(2)}×` : '—';
  $('standingPill').title = m
    ? `Average of actual ÷ estimated over ${n} finished task${n === 1 ? '' : 's'}. `
      + `You take ${(m * 100).toFixed(0)}% of the time you predict.`
    : 'Finish a task to start building your standing multiple.';
}

/* ── the prediction popup ── */
export let lastPredictedFor = null;   // remembers the estimate we last warned about

export function showPrediction(estMin, opts = {}) {
  if (!estMin || estMin <= 0) return;
  const m = standingMultiple();
  const mult = m || 1;
  const predMin = estMin * mult;

  $('predictTitle').textContent = m ? 'Reality check' : 'First estimate';
  $('predictBody').innerHTML = m
    ? `You will likely spend <span class="big">${fmtHours(predMin)}</span> on this — `
      + `not the <b>${fmtDurMin(estMin)}</b> you just entered.<br><br>`
      + `Your standing multiple is <b>${mult.toFixed(2)}×</b>, so studyHub scales every estimate you make by that much.`
    : `You estimated <b>${fmtDurMin(estMin)}</b>. With no finished tasks yet studyHub assumes a <b>1.00×</b> multiple, `
      + `so the prediction is also <span class="big">${fmtHours(predMin)}</span>. `
      + `Finish this task and the multiple starts learning how you really work.`;

  const meta = [];
  meta.push(`estimate ${fmtDurMin(estMin)} × ${mult.toFixed(2)} = ${fmtDurMin(predMin)}`);
  if (opts.scheduled && opts.start) {
    const end = opts.start + estMin * 60000;
    meta.push(`calendar block: ${fmtWhen(opts.start)} → ${new Date(end).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} (${fmtDurMin(estMin)})`);
    if (m && mult > 1.05) {
      meta.push(`⚠ the block covers the ESTIMATE, not the prediction — you may run ${fmtDurMin(predMin - estMin)} past it`);
    }
  }
  if (m) {
    const n = S.tasks.filter((t) => t.done && typeof t.multiple === 'number').length;
    meta.push(`based on ${n} finished task${n === 1 ? '' : 's'}`);
  }
  $('predictMeta').innerHTML = meta.map(escapeHtml).join('<br>');
  openModal('predictModal');
  lastPredictedFor = estMin;
}

export let formType = 'task';

export function estMinFromForm() { return (+$('fEstH').value || 0) * 60 + (+$('fEstM').value || 0); }

export function updateEstPreview() {
  const est = estMinFromForm();
  const m = standingMultiple();
  const box = $('estPreview');
  if (!est) { box.textContent = 'Enter an estimate to see your projected time.'; return; }
  const mult = m || 1;
  box.innerHTML = `Projected: <b>${fmtDurMin(est * mult)}</b> `
    + `(${fmtDurMin(est)} × ${mult.toFixed(2)}×${m ? '' : ' — no history yet'})`;
  updateSchedNote();
}

export function updateSchedNote() {
  const on = $('fSchedule').checked;
  $('schedField').hidden = !on;
  if (!on) return;
  const start = fromLocalInput($('fStart').value);
  const est = estMinFromForm();
  if (!start || !est) { $('schedNote').textContent = 'Pick a start time — the block will run exactly as long as your estimate.'; return; }
  const end = start + est * 60000;
  $('schedNote').textContent =
    `Blocks ${new Date(start).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
    + ` → ${new Date(end).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} = ${fmtDurMin(est)}`;
}

export function setFormType(type) {
  formType = type;
  [...$('typeSeg').children].forEach((b) => b.classList.toggle('is-active', b.dataset.type === type));
  $('taskOnly').hidden = type !== 'task';
  $('eventOnly').hidden = type !== 'event';
  $('deadlineOnly').hidden = type !== 'deadline';
  $('submitBtn').textContent = type === 'task' ? 'Add task' : type === 'event' ? 'Add event' : 'Add deadline';
  $('fTitle').placeholder = type === 'task'
    ? 'Problem set 4 — chapters 6–7'
    : type === 'event' ? 'Lecture · Stat 110'
    : 'Final essay due — HIST 1300';
  $('typeExplainer').innerHTML = type === 'task'
    ? '<b>TASK</b> — work you do. It carries a time estimate, a pausable timer, and produces a multiple when you finish it.'
    : type === 'event'
      ? '<b>EVENT</b> — a fixed commitment (lecture, section, office hours). It occupies real clock time, is never timed, and never affects your multiple.'
      : '<b>DEADLINE</b> — a moment something is due. No duration, never timed, never in your multiple. File tasks under it and studyHub adds up whether the work still fits.';
  if (type === 'deadline') updateDuePreview();
}

export function updateDuePreview() {
  const due = fromLocalInput($('fDue').value);
  const box = $('duePreview');
  if (!due) { box.textContent = 'Pick the moment it is due.'; return; }
  box.innerHTML = `Due <b>${escapeHtml(fmtWhen(due))}</b> — ${escapeHtml(fmtLeft(due - Date.now()))}. `
    + 'File tasks under it to see whether the work fits.';
}

export function renderCourseSelects() {
  const fill = (sel, withAll) => {
    const prev = sel.value;
    sel.textContent = '';
    if (withAll) sel.appendChild(new Option('All courses', ''));
    S.courses.forEach((c) => sel.appendChild(new Option(c.name, c.id)));
    sel.value = [...sel.options].some((o) => o.value === prev) ? prev : (withAll ? '' : (S.courses[0] || {}).id || '');
  };
  fill($('fCourse'), false);
  fill($('filterCourse'), true);
  $('filterCourse').value = S.ui.filterCourse || '';
  // the horizon and the week group by the same courses, so both follow them
  renderLtGroupFilter();
  renderWkGroupFilter();
}

export function initPanel() {
  $('panelToggle').addEventListener('click', () => {
    const opening = !document.body.classList.contains('panel-open');
    setDrawer(opening ? 'form' : null);
    if (opening) $('fTitle').focus();
  });
  $('panelClose').addEventListener('click', () => setDrawer(null));

  $('tasksToggle').addEventListener('click', () => {
    setDrawer(document.body.classList.contains('tasks-open') ? null : 'tasks');
  });
  $('tasksClose').addEventListener('click', () => setDrawer(null));

  $('sessClose').addEventListener('click', closeSession);

  [...$('typeSeg').children].forEach((b) =>
    b.addEventListener('click', () => setFormType(b.dataset.type)));

  [...$('listSeg').children].forEach((b) =>
    b.addEventListener('click', () => {
      S.ui.list = b.dataset.list; save();
      [...$('listSeg').children].forEach((x) => x.classList.toggle('is-active', x === b));
      renderList();
    }));

  $('filterCourse').addEventListener('change', (e) => { S.ui.filterCourse = e.target.value; save(); renderList(); });

  /* courses */
  $('addCourse').addEventListener('click', () => {
    askText('Add course', 'Course name', '', (name) => {
      if (S.courses.some((c) => c.name.toLowerCase() === name.toLowerCase())) return toast('That course already exists.', true);
      const c = { id: uid(), name, color: COURSE_COLORS[S.courses.length % COURSE_COLORS.length] };
      S.courses.push(c); save();
      renderCourseSelects();
      $('fCourse').value = c.id;
      toast(`Course “${name}” added`);
    });
  });
  $('delCourse').addEventListener('click', () => {
    const id = $('fCourse').value;
    const c = courseById(id);
    if (!c) return;
    if (S.courses.length === 1) return toast('Keep at least one course.', true);
    const used = S.tasks.filter((t) => t.courseId === id).length + S.events.filter((e) => e.courseId === id).length;
    askText('Remove course', `Type YES to remove “${c.name}”${used ? ` — ${used} item(s) will keep the name but lose the link` : ''}`, '', (v) => {
      if (v.toUpperCase() !== 'YES') return;
      S.courses = S.courses.filter((x) => x.id !== id);
      save(); renderCourseSelects(); renderList();
      toast(`Removed “${c.name}”`);
    });
  });

  /* estimate → popup */
  ['fEstH', 'fEstM'].forEach((id) => {
    $(id).addEventListener('input', updateEstPreview);
    $(id).addEventListener('change', () => {
      updateEstPreview();
      const est = estMinFromForm();
      if (est > 0 && est !== lastPredictedFor) {
        showPrediction(est, { scheduled: $('fSchedule').checked, start: fromLocalInput($('fStart').value) });
      }
    });
  });

  $('fSchedule').addEventListener('change', () => {
    updateSchedNote();
    if ($('fSchedule').checked && !$('fStart').value) {
      const d = new Date(Date.now() + 3600e3);
      d.setMinutes(0, 0, 0);
      $('fStart').value = toLocalInput(d);
      updateSchedNote();
    }
  });
  $('fStart').addEventListener('change', updateSchedNote);

  /* submit */
  $('itemForm').addEventListener('submit', (e) => {
    e.preventDefault();
    const title = $('fTitle').value.trim();
    if (!title) return;
    const courseId = $('fCourse').value;

    if (formType === 'task') {
      const est = estMinFromForm();
      if (est <= 0) return toast('Give the task a time estimate.', true);

      let start = null;
      if ($('fSchedule').checked) {
        start = fromLocalInput($('fStart').value);
        if (!start) return toast('Pick a start time for the calendar block.', true);
      }

      const t = {
        id: uid(), kind: 'task', title, courseId,
        estMin: est,
        accSec: 0, runSince: null,
        done: false, multiple: null,
        actualMin: null,
        subtasks: [],
        deadlineId: $('fDeadline').value || null,
        start,                        // when scheduled: block = start … start + estMin
        created: Date.now(), completedAt: null,
      };
      S.tasks.push(t);
      touchPlan();
      save();
      renderList(); renderStats(); renderScheduled(); renderClock();
      refreshPlanningTabs();

      // the popup appears for every route into a task, scheduled or not
      showPrediction(est, { scheduled: !!start, start });
      lastPredictedFor = null;

      /* a promoted wandering task keeps its place on the board, marked as
         having crossed over rather than silently disappearing */
      if (pendingPromotion) {
        const note = noteById(pendingPromotion.noteId);
        const w = note && note.tasks.find((x) => x.id === pendingPromotion.wtId);
        if (w) { w.taskId = t.id; save(); renderNote(note); updateThoughtChrome(); }
        clearPendingPromotion();
      }

      $('fTitle').value = '';
      $('fTitle').focus();
      const dl = deadlineById(t.deadlineId);
      toast(dl ? `Task added under “${dl.title}”` : start ? 'Task added and blocked out' : 'Task added');
    } else if (formType === 'deadline') {
      const d = addDeadlineFromForm(title, courseId);
      if (!d) return;
      save();
      S.ui.list = 'deadlines';
      [...$('listSeg').children].forEach((x) => x.classList.toggle('is-active', x.dataset.list === 'deadlines'));
      renderList(); renderScheduled(); renderCalendar(); renderDeadlineSelects();
      refreshPlanningTabs();
      $('fTitle').value = ''; $('fDueNotes').value = '';
      toast('Deadline added — file tasks under it to see if the work fits');
    } else {
      const st = fromLocalInput($('fEvStart').value);
      const en = fromLocalInput($('fEvEnd').value);
      if (!st || !en) return toast('Events need a start and an end.', true);
      if (en <= st) return toast('The event ends before it starts.', true);
      const ev = {
        id: uid(), kind: 'event', title, courseId,
        start: st, end: en,
        notes: $('fEvNotes').value.trim(),
        created: Date.now(),
      };
      S.events.push(ev);
      // an hour with an event in it is an hour the week cannot plan into
      touchPlan();
      save();
      S.ui.list = 'events';
      [...$('listSeg').children].forEach((x) => x.classList.toggle('is-active', x.dataset.list === 'events'));
      renderList(); renderStats(); renderScheduled();
      refreshPlanningTabs();
      $('fTitle').value = ''; $('fEvNotes').value = '';
      toast('Event added — not timed, not counted in your multiple');
    }
  });

  /* deadlines */
  $('fDue').addEventListener('input', updateDuePreview);
  renderDeadlineSelects();

  /* timer mode */
  [...$('timerModeSeg').children].forEach((b) =>
    b.addEventListener('click', () => setTimerMode(b.dataset.mode)));
  /* The pomodoro settings are also the planner's session bounds: the shortest
     stretch that can hold work is one work interval, and the longest block it
     will ever ask for is one full long-break cycle. Changing either changes
     the shape of every block in the week. */
  $('pomoWork').addEventListener('change', () => {
    S.timer.workMin = clamp(+$('pomoWork').value || 25, 1, 120);
    touchPlan();
    save(); renderTimerMode(); renderList();
  });
  $('pomoBreak').addEventListener('change', () => {
    S.timer.breakMin = clamp(+$('pomoBreak').value || 5, 1, 60);
    touchPlan();
    save(); renderTimerMode();
  });
  renderTimerMode(); renderPomo();

  /* edit an existing task */
  $('editConfirm').addEventListener('click', confirmEdit);
  ['eEstH', 'eEstM', 'eStart'].forEach((id) => $(id).addEventListener('input', updateEditNote));
  $('eSchedule').addEventListener('change', () => {
    $('eSchedField').hidden = !$('eSchedule').checked;
    if ($('eSchedule').checked && !$('eStart').value) {
      const d = new Date(Date.now() + 3600e3); d.setMinutes(0, 0, 0);
      $('eStart').value = toLocalInput(d);
    }
    updateEditNote();
  });

  /* timers */
  setInterval(tickTimers, 250);
}

export function startTask(id) {
  /* A task generated from a series has to say what it is made of before it can
     be timed, because that description *is* the measurement — forty pages
     timed anonymously teaches the app nothing. Ordinary tasks have no schema
     and fall straight through, so nothing else in the app gains a click. */
  const pending = S.tasks.find((x) => x.id === id);
  if (needsIntake(pending)) { openIntake(id); return; }

  S.tasks.forEach((t) => {                      // only one timer runs at a time
    if (t.runSince && t.id !== id) {
      t.accSec = elapsedSec(t); t.runSince = null;
    }
  });
  const t = S.tasks.find((x) => x.id === id);
  if (!t || t.done) return;
  t.runSince = Date.now();
  // starting work ends any break, and always opens a fresh pomodoro
  if (pomoOn()) { S.pomo.until = 0; beginPomodoro(t); }
  save(); renderList(); renderClock(); renderPomo();
}
export function pauseTask(id) {
  const t = S.tasks.find((x) => x.id === id);
  if (!t || !t.runSince) return;
  t.accSec = elapsedSec(t); t.runSince = null;
  save(); renderList(); renderClock();
}

/* ── pomodoro ──
   The mode changes when the clock stops, never what the clock counts. A task's
   accSec is work time and nothing else: a pomodoro ending pauses the task, so
   the break banks no time, and a break ending never auto-starts the next
   interval — the app must not log minutes while you are still away from the
   desk. Everything the multiple sees is identical in both modes. */

export const pomoOn = () => S.timer.mode === 'pomodoro';
export const pomoLeft = () => Math.max(0, S.pomo.until - Date.now());

/** Seconds of work left in the current pomodoro, or null when one isn't running. */
export function pomoWorkLeft() {
  if (!pomoOn() || S.pomo.phase !== 'work') return null;
  const t = S.tasks.find((x) => x.id === S.pomo.taskId);
  if (!t || !t.runSince) return null;
  return Math.max(0, S.timer.workMin * 60 - (elapsedSec(t) - S.pomo.anchorSec));
}

export function resetPomo(keepCount = false) {
  S.pomo = { phase: 'idle', taskId: null, anchorSec: 0, until: 0, done: keepCount ? S.pomo.done : 0 };
}

/** Called when a task starts running; anchors a fresh pomodoro to its banked time. */
export function beginPomodoro(t) {
  if (!pomoOn()) return;
  S.pomo.phase = 'work';
  S.pomo.taskId = t.id;
  S.pomo.anchorSec = t.accSec || 0;
  S.pomo.until = 0;
}

/** A pomodoro filled up: bank the time, stop the clock, start the break. */
export function endPomodoro(t) {
  const long = (S.pomo.done + 1) % S.timer.perLong === 0;
  pauseTask(t.id);                       // banks accSec — the break counts for nothing
  S.pomo.done += 1;
  S.pomo.phase = long ? 'long' : 'break';
  S.pomo.until = Date.now() + (long ? S.timer.longMin : S.timer.breakMin) * 60000;
  save();
  toast(`🍅 ${S.pomo.done} done — take ${long ? 'a long' : 'a'} ${long ? S.timer.longMin : S.timer.breakMin}-minute break.`);
  renderPomo(); renderList(); renderClock();
}

export function endBreak(silent) {
  S.pomo.phase = 'idle';
  S.pomo.until = 0;
  save();
  if (!silent) toast('Break over — press Start when you are back at the desk.');
  renderPomo(); renderList(); renderClock();
}

export function setTimerMode(mode) {
  S.timer.mode = mode === 'pomodoro' ? 'pomodoro' : 'normal';
  if (S.timer.mode === 'normal') resetPomo();
  else {
    const run = S.tasks.find((t) => t.runSince);
    if (run) beginPomodoro(run);         // switching mid-task starts a pomodoro from here
  }
  save();
  renderTimerMode(); renderPomo(); renderList(); renderClock();
}

export function renderTimerMode() {
  [...$('timerModeSeg').children].forEach((b) =>
    b.classList.toggle('is-active', b.dataset.mode === S.timer.mode));
  $('pomoConfig').hidden = !pomoOn();
  $('pomoWork').value = S.timer.workMin;
  $('pomoBreak').value = S.timer.breakMin;
  $('pomoNote').textContent = `long break ${S.timer.longMin}m every ${S.timer.perLong}`;
}

/** Draws one break banner. Work-phase countdowns live on the task row itself. */
export function fillBreakBanner(bar) {
  bar.textContent = '';
  const long = S.pomo.phase === 'long';
  bar.appendChild(el('span', 'pomo-icon', long ? '🌿' : '☕'));
  const txt = el('span', 'pomo-text');
  txt.appendChild(el('strong', null, `${long ? 'Long break' : 'Break'} · ${fmtClock(pomoLeft() / 1000)}`));
  txt.appendChild(el('em', null, `${S.pomo.done} pomodoro${S.pomo.done === 1 ? '' : 's'} done — the clock is stopped`));
  bar.appendChild(txt);
  const skip = el('button', 'btn btn-ghost', 'Skip');
  skip.addEventListener('click', () => endBreak(true));
  bar.appendChild(skip);
}

/** A break is one fact, shown in both places it could be read from. */
export function renderPomo() {
  const onBreak = pomoOn() && (S.pomo.phase === 'break' || S.pomo.phase === 'long');
  ['pomoBanner', 'sessBreak'].forEach((id) => {
    const bar = $(id);
    if (!bar) return;
    bar.hidden = !onBreak;
    if (onBreak) fillBreakBanner(bar);
  });
}

export function tickTimers() {
  const run = S.tasks.find((t) => t.runSince);

  /* drive the pomodoro off the same tick the timers already use */
  if (pomoOn()) {
    if (run && S.pomo.phase === 'work' && S.pomo.taskId === run.id) {
      if (pomoWorkLeft() === 0) endPomodoro(run);
    } else if (S.pomo.phase === 'break' || S.pomo.phase === 'long') {
      if (pomoLeft() === 0) endBreak();
      else renderPomo();
    }
    document.querySelectorAll('[data-pomo]').forEach((node) => {
      const left = pomoWorkLeft();
      node.textContent = left == null ? '' : `🍅 ${fmtClock(left)} left in this pomodoro`;
    });
  }

  /* Every live read-out is found by data attribute, not by which view drew it:
     a panel row, a dock row and the session frame all update off this one tick.
     `[data-task]` on the enclosing element is how each read-out finds its bar. */
  document.querySelectorAll('[data-timer]').forEach((n) => {
    const node = /** @type {HTMLElement} */ (n);
    const t = S.tasks.find((x) => x.id === node.dataset.timer);
    if (!t) return;
    const sec = elapsedSec(t);
    node.textContent = fmtClock(sec);
    node.classList.toggle('is-over', sec / 60 > t.estMin);
    const bar = node.closest('[data-task]')?.querySelector('.bar > i');
    if (bar) setBar(bar, sec / 60, t.estMin);
  });

  /* the day dock counts in minutes rather than clock time */
  document.querySelectorAll('[data-devoted]').forEach((n) => {
    const node = /** @type {HTMLElement} */ (n);
    const t = S.tasks.find((x) => x.id === node.dataset.devoted);
    if (!t) return;
    node.textContent = fmtDurMin(devotedMin(t));
    const bar = node.closest('[data-task]')?.querySelector('.bar > i');
    if (bar) setBar(bar, devotedMin(t), t.estMin);
  });

  if (run) refreshDayTotal();
  if (run && $('tab-clock').classList.contains('is-active')) renderClock();
}

export function setBar(i, actualMin, estMin) {
  const r = estMin ? actualMin / estMin : 0;
  i.style.width = Math.min(100, r * 100) + '%';
  i.classList.toggle('is-over', r > 1 && r <= 1.75);
  i.classList.toggle('is-way-over', r > 1.75);
}

/* ── subtasks ──
   A checklist, deliberately not a second class of task: subtasks carry no
   estimate and no timer. The multiple is the whole point of this app, and it
   only means something if one estimate maps to one logged actual — giving each
   step its own estimate would fork that arithmetic (does the parent equal the
   sum? what multiple does a step produce?). So the parent keeps the single
   estimate, and these just track what is left to do inside it. */

export const subTotal = (t) => (t.subtasks || []).length;
export const subDone  = (t) => (t.subtasks || []).filter((s) => s.done).length;

export function addSubtask(id) {
  const t = S.tasks.find((x) => x.id === id);
  if (!t) return;
  askText('Add subtask', `A step inside “${t.title}”`, '', (title) => {
    t.subtasks.push({ id: uid(), title, done: false });
    S.ui.subsCollapsed = S.ui.subsCollapsed.filter((x) => x !== t.id);   // adding one reveals the list
    save(); renderList(); renderScheduled();
  });
}

export function toggleSubtask(taskId, subId) {
  const t = S.tasks.find((x) => x.id === taskId);
  const s = t && t.subtasks.find((y) => y.id === subId);
  if (!s) return;
  s.done = !s.done;
  save(); renderList(); renderScheduled();
  if (s.done && subDone(t) === subTotal(t)) {
    toast(`Every step of “${t.title}” is ticked — Finish it to log the time.`);
  }
}

export function removeSubtask(taskId, subId) {
  const t = S.tasks.find((x) => x.id === taskId);
  if (!t) return;
  t.subtasks = t.subtasks.filter((y) => y.id !== subId);
  save(); renderList(); renderScheduled();
}

export function toggleSubsCollapsed(id) {
  S.ui.subsCollapsed = S.ui.subsCollapsed.includes(id)
    ? S.ui.subsCollapsed.filter((x) => x !== id)
    : S.ui.subsCollapsed.concat(id);
  save(); renderList();
}

/** The checklist under a task row: header, the steps themselves, add button. */
export function subtaskBlock(t) {
  const wrap = el('div', 'subs');
  const total = subTotal(t);
  const collapsed = S.ui.subsCollapsed.includes(t.id);

  const head = el('div', 'subs-head');
  if (total) {
    const tog = el('button', 'subs-toggle', `${collapsed ? '▸' : '▾'} ${subDone(t)}/${total} steps`);
    tog.title = collapsed ? 'Show the checklist' : 'Fold the checklist away';
    tog.addEventListener('click', () => toggleSubsCollapsed(t.id));
    head.appendChild(tog);
  }
  // with no steps the row stays quiet — the add button says everything a
  // "no steps yet" label would, without a line of chrome on every task
  head.appendChild(el('span', 'spacer'));
  if (!t.done) {
    const add = el('button', 'sub-add', '＋ Subtask');
    add.title = 'Break this task into steps';
    add.addEventListener('click', () => addSubtask(t.id));
    head.appendChild(add);
  }
  wrap.appendChild(head);

  if (total && !collapsed) {
    const list = el('div', 'sub-list');
    t.subtasks.forEach((s) => {
      const row = el('label', 'sub' + (s.done ? ' is-done' : ''));

      const box = el('input', 'sub-check');
      box.type = 'checkbox';
      box.checked = s.done;
      box.addEventListener('change', () => toggleSubtask(t.id, s.id));
      row.appendChild(box);

      row.appendChild(el('span', 'sub-title', s.title));

      const x = el('button', 'item-x sub-x', '✕');
      x.title = 'Delete this step';
      // the row is a <label>, so a click inside it would also hit the checkbox
      x.addEventListener('click', (e) => { e.preventDefault(); removeSubtask(t.id, s.id); });
      row.appendChild(x);

      list.appendChild(row);
    });
    wrap.appendChild(list);
  }
  return wrap;
}

/* ── deadlines ──
   The third kind of entry. A task is work you do, an event is time that is
   spoken for, a deadline is a moment something is due — it has no duration, is
   never timed, and never touches your multiple.

   What makes it worth having is the arithmetic underneath: tasks point at a
   deadline, so studyHub can add up the work still owed to it, scale that by
   your standing multiple, and compare it against the time actually left. That
   is the question a due date can't answer on its own — not "when is it due"
   but "is what I have left going to fit". */

export function fmtLeft(ms) {
  const abs = Math.abs(ms);
  const mins = Math.round(abs / 60000);
  const s = mins < 60 ? `${mins}m`
    : mins < 1440 ? `${Math.round(mins / 60)}h`
    : `${Math.round(mins / 1440)}d`;
  return ms < 0 ? `${s} overdue` : `${s} left`;
}

export function addDeadlineFromForm(title, courseId) {
  const due = fromLocalInput($('fDue').value);
  if (!due) { toast('A deadline needs a due date.', true); return null; }
  const d = {
    id: uid(), kind: 'deadline', title, courseId, due,
    notes: $('fDueNotes').value.trim(),
    created: Date.now(),
  };
  S.deadlines.push(d);
  /* A deadline typed here is a moment the horizon and the week both have to
     answer for, even though neither tab is on screen. */
  touchPlan();
  return d;
}

/** Fills every deadline picker; keeps the current value when it still exists. */
export function renderDeadlineSelects() {
  const fill = (sel, current) => {
    if (!sel) return;
    sel.textContent = '';
    sel.appendChild(new Option('— none —', ''));
    S.deadlines
      .slice()
      .sort((a, b) => a.due - b.due)
      .forEach((d) => sel.appendChild(new Option(`${d.title} · ${fmtWhen(d.due)}`, d.id)));
    sel.value = current && S.deadlines.some((d) => d.id === current) ? current : '';
  };
  fill($('fDeadline'), $('fDeadline') ? $('fDeadline').value : '');
  const t = S.tasks.find((x) => x.id === editId);
  fill($('eDeadline'), t ? t.deadlineId : '');
}

/** Opens the task panel on the deadline list — where a deadline chip on the grid leads. */
export function showDeadlineList() {
  setDrawer('tasks');
  S.ui.list = 'deadlines'; save();
  [...$('listSeg').children].forEach((x) => x.classList.toggle('is-active', x.dataset.list === 'deadlines'));
  renderList();
}

/** Opens the panel form ready to add a task that belongs to this deadline. */
export function planForDeadline(id) {
  const d = deadlineById(id);
  if (!d) return;
  setDrawer('form');
  setFormType('task');
  renderDeadlineSelects();
  $('fDeadline').value = d.id;
  $('fCourse').value = d.courseId;
  $('fTitle').value = '';
  $('fTitle').focus();
  $('fTitle').scrollIntoView({ block: 'nearest' });
  toast(`Next task will be filed under “${d.title}”`);
}

export function deadlineCard(d) {
  const load = deadlineLoad(d);
  const overdue = load.msLeft < 0;
  const n = el('div', 'item dl-item' + (overdue ? ' is-overdue' : ''));
  n.style.setProperty('--course', courseColor(d.courseId));

  const top = el('div', 'item-top');
  top.appendChild(el('div', 'item-title', '⚑ ' + d.title));
  const x = el('button', 'item-x', '✕');
  x.title = 'Delete deadline (its tasks stay, unlinked)';
  x.addEventListener('click', () => {
    S.deadlines = S.deadlines.filter((y) => y.id !== d.id);
    S.tasks.forEach((t) => { if (t.deadlineId === d.id) t.deadlineId = null; });
    touchPlan();
    save(); renderList(); renderScheduled(); renderCalendar(); renderDeadlineSelects();
    refreshPlanningTabs();
  });
  top.appendChild(x);
  n.appendChild(top);

  const b = el('div', 'badges');
  b.appendChild(el('span', 'badge kind-deadline', 'deadline'));
  b.appendChild(el('span', 'badge course', courseName(d.courseId)));
  b.appendChild(el('span', 'badge sched', '⚑ ' + fmtWhen(d.due)));
  b.appendChild(el('span', 'badge dl-left' + (overdue ? ' is-bad' : ''), fmtLeft(load.msLeft)));
  n.appendChild(b);

  if (d.notes) {
    const p = el('div', 'est-tag', d.notes);
    p.style.marginTop = '7px';
    n.appendChild(p);
  }

  /* the verdict: what is still owed, against what is still available */
  const v = el('div', 'dl-verdict');
  if (!load.linked.length) {
    v.appendChild(el('span', 'est-tag', 'No tasks filed under this yet.'));
  } else {
    const line = el('div', 'dl-sum');
    line.appendChild(el('strong', null, fmtDurMin(load.projLeft)));
    line.appendChild(el('span', null,
      ` of work left across ${load.open.length} open task${load.open.length === 1 ? '' : 's'}`
      + `${load.doneCount ? ` · ${load.doneCount} done` : ''}`));
    v.appendChild(line);

    const m = standingMultiple();
    const verdict = el('div', 'dl-fit' + (load.fits ? ' is-ok' : ' is-bad'));
    verdict.textContent = !load.open.length
      ? 'Everything filed under this is finished.'
      : load.fits
        ? `Fits — ${fmtDurMin(load.projLeft)} of work, ${fmtLeft(load.msLeft)}.`
        : overdue
          ? `Overdue with ${fmtDurMin(load.projLeft)} still to do.`
          : `Tight — ${fmtDurMin(load.projLeft)} of work but only ${fmtLeft(load.msLeft)}.`;
    verdict.title = m
      ? `Estimates scaled by your ${m.toFixed(2)}× standing multiple, minus time already logged.`
      : 'No history yet, so estimates are taken at face value.';
    v.appendChild(verdict);
  }
  n.appendChild(v);

  if (load.linked.length) {
    const list = el('div', 'dl-tasks');
    load.linked
      .slice()
      .sort((a, b) => (a.done ? 1 : 0) - (b.done ? 1 : 0) || a.created - b.created)
      .forEach((t) => {
        const row = el('div', 'dl-task' + (t.done ? ' is-done' : ''));
        row.appendChild(el('span', 'dl-task-mark', t.done ? '☑' : '☐'));
        row.appendChild(el('span', 'dl-task-title', t.title));
        row.appendChild(el('span', 'dl-task-est', t.done ? fmtDurMin(t.actualMin) : fmtDurMin(t.estMin)));
        list.appendChild(row);
      });
    n.appendChild(list);
  }

  const row = el('div', 'timer-row');
  const note = el('span', 'est-tag', 'not timed');
  note.title = 'A deadline is a moment, not work — it never enters your multiple.';
  row.appendChild(note);
  row.appendChild(el('span', 'spacer'));
  const plan = el('button', 'btn btn-primary', '＋ Plan a task');
  plan.title = 'Add a task filed under this deadline';
  plan.addEventListener('click', () => planForDeadline(d.id));
  row.appendChild(plan);
  n.appendChild(row);
  return n;
}

/* ── editing a task after it exists ──
   Title, course, schedule and estimate are all editable, but the estimate is
   special: the multiple is actual ÷ estimate, so revising the estimate upward
   once you are already running over would quietly launder a bad prediction
   into a good one, and the standing multiple — the only thing in this app that
   knows how you really work — would drift toward a meaningless 1.00×.

   So on a task you have already put time into, the estimate you first
   committed to is kept as `baselineEstMin` and the multiple is measured
   against THAT. `estMin` becomes the working plan: it drives the progress bar
   and the calendar block, which should track reality. A genuine typo can still
   be corrected — that is what the re-baseline checkbox is for — but it is an
   explicit choice rather than a silent one. */

export let editId = null;

export function editEstMin() { return (+$('eEstH').value || 0) * 60 + (+$('eEstM').value || 0); }

export function openEdit(id) {
  const t = S.tasks.find((x) => x.id === id);
  if (!t) return;
  editId = id;

  $('editWhich').innerHTML = wasStarted(t)
    ? `<b>${escapeHtml(t.title)}</b> — ${fmtClock(elapsedSec(t))} logged so far.`
    : `<b>${escapeHtml(t.title)}</b> — not started yet.`;

  $('eTitle').value = t.title;
  const sel = $('eCourse');
  sel.textContent = '';
  S.courses.forEach((c) => sel.appendChild(new Option(c.name, c.id)));
  sel.value = t.courseId;

  renderDeadlineSelects();
  $('eEstH').value = Math.floor(t.estMin / 60);
  $('eEstM').value = t.estMin % 60;
  $('eRebase').checked = false;
  $('eSchedule').checked = !!t.start;
  $('eSchedField').hidden = !t.start;
  $('eStart').value = t.start ? toLocalInput(t.start) : '';

  updateEditNote();
  openModal('editModal');
  setTimeout(() => $('eTitle').focus(), 30);
}

export function updateEditNote() {
  const t = S.tasks.find((x) => x.id === editId);
  if (!t) return;
  const est = editEstMin();
  const base = estBaseline(t);
  const changed = est !== t.estMin;
  const started = wasStarted(t);

  // the re-baseline choice only exists when it would actually change something
  const show = changed && started && est > 0;
  $('editBaseline').hidden = !show;
  if (show) {
    $('editBaselineNote').innerHTML =
      `You have already logged time against this task, so your multiple stays measured against `
      + `the <b>${escapeHtml(fmtDurMin(base))}</b> you first committed to. `
      + `The new ${escapeHtml(fmtDurMin(est))} becomes the plan — progress bar and calendar block.`;
  }

  const lines = [];
  const m = standingMultiple();
  if (est > 0) lines.push(`estimate ${fmtDurMin(est)} × ${(m || 1).toFixed(2)} = ${fmtDurMin(est * (m || 1))} projected`);
  if (started) lines.push(`${fmtClock(elapsedSec(t))} already logged`);
  if (!changed && base !== t.estMin) lines.push(`multiple measured against ${fmtDurMin(base)} (original)`);
  if ($('eSchedule').checked) {
    const st = fromLocalInput($('eStart').value);
    if (st && est > 0) {
      const end = st + est * 60000;
      lines.push(`blocks ${fmtWhen(st)} → ${new Date(end).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`);
    }
  }
  $('editMeta').innerHTML = lines.map(escapeHtml).join('<br>');
}

export function confirmEdit() {
  const t = S.tasks.find((x) => x.id === editId);
  if (!t) return;
  const title = $('eTitle').value.trim();
  if (!title) return toast('The task needs a title.', true);
  const est = editEstMin();
  if (est <= 0) return toast('The task needs a time estimate.', true);

  let start = null;
  if ($('eSchedule').checked) {
    start = fromLocalInput($('eStart').value);
    if (!start) return toast('Pick a start time for the calendar block.', true);
  }

  const estChanged = est !== t.estMin;
  if (estChanged) {
    if ($('eRebase').checked) delete t.baselineEstMin;          // treat it as a correction
    else if (wasStarted(t)) t.baselineEstMin = estBaseline(t);  // freeze the first commitment
  }

  t.title = title;
  t.courseId = $('eCourse').value;
  t.deadlineId = $('eDeadline').value || null;
  t.estMin = est;
  t.start = start;
  /* Every field above is one the planner reads: the estimate is what it costs,
     the deadline is when it is owed, and a start time takes it out of the
     queue and turns it into capacity. */
  touchPlan();
  save();

  closeModal('editModal');
  renderList(); renderStats(); renderScheduled(); renderCalendar(); renderClock();
  refreshPlanningTabs();

  toast(estChanged && t.baselineEstMin
    ? `Saved — plan now ${fmtDurMin(est)}, multiple still measured against ${fmtDurMin(t.baselineEstMin)}`
    : 'Task updated');
}

/* ── finishing a task ── */
export let finishId = null;

export function openFinish(id) {
  const t = S.tasks.find((x) => x.id === id);
  if (!t) return;
  if (t.runSince) { t.accSec = elapsedSec(t); t.runSince = null; save(); }
  finishId = id;
  const mins = Math.max(1, Math.round(elapsedSec(t) / 60));
  $('finishTaskName').innerHTML = `<b>${escapeHtml(t.title)}</b> · ${escapeHtml(courseName(t.courseId))}`;
  const base = estBaseline(t);
  $('finishEst').textContent = fmtDurMin(base);
  $('finishEstNote').hidden = base === t.estMin;
  if (base !== t.estMin) $('finishEstNote').textContent = `plan later revised to ${fmtDurMin(t.estMin)}`;

  /* Finishing with steps left open is allowed — the checklist is a memory aid,
     not a gate — but say so rather than letting it pass silently. */
  const open = subTotal(t) - subDone(t);
  $('finishSubs').hidden = !open;
  if (open) {
    $('finishSubs').textContent =
      `${open} of ${subTotal(t)} steps ${open === 1 ? 'is' : 'are'} still unticked. `
      + 'Logging the time finishes the whole task anyway.';
  }

  $('finishH').value = Math.floor(mins / 60);
  $('finishM').value = mins % 60;
  updateFinishMult();
  openModal('finishModal');
}

export function finishActualMin() { return (+$('finishH').value || 0) * 60 + (+$('finishM').value || 0); }

export function updateFinishMult() {
  const t = S.tasks.find((x) => x.id === finishId);
  if (!t) return;
  const act = finishActualMin();
  const out = $('finishMult');
  if (!act || !estBaseline(t)) { out.textContent = '—'; out.className = 'mult-big'; return; }
  const m = act / estBaseline(t);
  out.textContent = m.toFixed(2) + '×';
  out.className = 'mult-big ' + multClass(m);
}

export function confirmFinish() {
  const t = S.tasks.find((x) => x.id === finishId);
  if (!t) return;
  const act = finishActualMin();
  if (act <= 0) return toast('Log at least a minute.', true);

  const before = standingMultiple();
  t.actualMin = act;
  t.accSec = act * 60;
  t.runSince = null;
  t.multiple = act / estBaseline(t);   // always the first commitment, never a revised one
  t.done = true;
  t.completedAt = Date.now();
  /* Twice over: the work leaves the queue, and the time it really took joins
     the evidence every other estimate is priced against. */
  touchPlan();
  save();

  const after = standingMultiple();
  closeModal('finishModal');
  renderList(); renderStats(); renderStanding(); renderScheduled(); renderClock();
  refreshPlanningTabs();

  /* The measurement, and what it changes. Nothing that grades the person.
     There were three tiers here — an icon and an adjective keyed off 1.1× and
     1.75× — and they were the only place in studyHub that passed judgement on
     her rather than on a plan. Two things argue against them. The app's own
     rule is that a measurement and a guess must never look alike, and a tier
     boundary at 1.1 is a guess wearing a measurement's clothes. And the
     estimate is the single number every prediction in the app is built from,
     so the last thing that should attach to it is a face at the moment of
     handing it over: the cheapest way to stop seeing 🐢 is to stop finishing
     tasks, or to pad the estimate until the number flatters. Either one costs
     the ladder its evidence at the source. */
  const m = t.multiple;
  $('resultBody').innerHTML =
    `<b>${escapeHtml(t.title)}</b> took <b>${fmtDurMin(act)}</b> against a <b>${fmtDurMin(t.estMin)}</b> estimate — `
    + `a multiple of <span class="big">${m.toFixed(2)}×</span>.`;
  const lines = [`this task: ${fmtDurMin(act)} ÷ ${fmtDurMin(t.estMin)} = ${m.toFixed(2)}×`];
  if (before) lines.push(`standing multiple: ${before.toFixed(2)}× → ${after.toFixed(2)}×`);
  else lines.push(`standing multiple: ${after.toFixed(2)}× (first data point)`);
  lines.push(`next time you estimate 1h, expect ${fmtDurMin(60 * after)}`);
  $('resultMeta').innerHTML = lines.map(escapeHtml).join('<br>');
  openModal('resultModal');
}

/* ── lists ──
   Tasks are now read in three places at once: the Current tasks panel, the day
   dock on the landing page, and the open session. renderList() is what the rest
   of the app already called after every mutation, so it stays the single
   entry point and fans out — no caller needs to know which views are on screen. */

export function renderList() {
  renderTaskList();
  renderDayDock();
  renderSession();
  renderTasksCount();
}

/** How many open tasks the Current tasks button advertises. */
export function renderTasksCount() {
  const open = S.tasks.filter((t) => !t.done).length;
  $('tasksCount').textContent = open;
  $('tasksToggle').title = open
    ? `${open} open task${open === 1 ? '' : 's'} in this session`
    : 'Nothing open yet — press ＋ New to add a task';
}

export function renderTaskList() {
  const wrap = $('panelList');
  wrap.textContent = '';
  const filter = S.ui.filterCourse;
  const byCourse = (x) => !filter || x.courseId === filter;

  let items = [];
  if (S.ui.list === 'active') items = S.tasks.filter((t) => !t.done).filter(byCourse)
    .sort((a, b) => (b.runSince ? 1 : 0) - (a.runSince ? 1 : 0) || (a.start || Infinity) - (b.start || Infinity) || a.created - b.created);
  else if (S.ui.list === 'done') items = S.tasks.filter((t) => t.done).filter(byCourse)
    .sort((a, b) => b.completedAt - a.completedAt);
  else if (S.ui.list === 'deadlines') items = S.deadlines.filter(byCourse).sort((a, b) => a.due - b.due);
  else items = S.events.filter(byCourse).sort((a, b) => a.start - b.start);

  if (!items.length) {
    const msg = S.ui.list === 'active' ? 'No open tasks. Press ＋ New — the popup will tell you how long it will really take.'
      : S.ui.list === 'done' ? 'Nothing finished yet. Your standing multiple appears here once you log a task.'
      : S.ui.list === 'deadlines' ? 'No deadlines yet. Press ＋ New to add one, then file tasks under it — studyHub will tell you whether the work still fits.'
      : 'No events yet. Events are fixed commitments; they are never timed and never change your multiple.';
    wrap.appendChild(el('div', 'empty-list', msg));
    return;
  }
  items.forEach((it) => wrap.appendChild(
    S.ui.list === 'events' ? eventRow(it)
      : S.ui.list === 'deadlines' ? deadlineCard(it)
      : taskRow(it)));
}

export function taskRow(t) {
  const n = el('div', 'item' + (t.done ? ' is-done' : '') + (t.runSince ? ' is-running' : ''));
  n.dataset.task = t.id;
  n.style.setProperty('--course', courseColor(t.courseId));

  const top = el('div', 'item-top');
  top.appendChild(el('div', 'item-title', t.title));

  const full = el('button', 'item-x item-full', '⛶');
  full.title = 'Open as a session — full frame, timer and pomodoro';
  full.addEventListener('click', () => openSession(t.id));
  top.appendChild(full);

  if (!t.done) {
    const ed = el('button', 'item-x item-edit', '✎');
    ed.title = 'Edit title, course, estimate or schedule';
    ed.addEventListener('click', () => openEdit(t.id));
    top.appendChild(ed);
  }
  const x = el('button', 'item-x', '✕');
  x.title = 'Delete task';
  x.addEventListener('click', () => {
    S.tasks = S.tasks.filter((y) => y.id !== t.id);
    touchPlan();
    save(); renderList(); renderStats(); renderStanding(); renderScheduled(); renderClock();
    refreshPlanningTabs();
  });
  top.appendChild(x);
  n.appendChild(top);

  const b = el('div', 'badges');
  b.appendChild(el('span', 'badge kind-task', 'task'));
  b.appendChild(el('span', 'badge course', courseName(t.courseId)));
  if (t.start) b.appendChild(el('span', 'badge sched', '📅 ' + fmtWhen(t.start)));
  /* A generated instance keeps no due date of its own anywhere else in the app,
     so this badge is the only place its deadline is legible from the task list. */
  if (t.series && t.series.due) {
    const sb = el('span', 'badge series' + (!t.done && t.series.due < Date.now() ? ' is-bad' : ''),
      '↻ ' + fmtWhen(t.series.due));
    sb.title = `From a recurring series — ${fmtLeft(t.series.due - Date.now())}`;
    b.appendChild(sb);
  }
  if (t.attrs) {
    const q = quantityAttr(t.series ? t.series.kind : '');
    const bits = choiceAttrs(t.series ? t.series.kind : '').map((a) => t.attrs[a.key]).filter(Boolean);
    if (q && t.attrs[q.key]) bits.push(`${t.attrs[q.key]} ${q.plural}`);
    if (bits.length) b.appendChild(el('span', 'badge attrs', bits.join(' · ')));
  }
  const dl = deadlineById(t.deadlineId);
  if (dl) {
    const db = el('span', 'badge dl-link' + (dl.due < Date.now() ? ' is-bad' : ''), '⚑ ' + dl.title);
    db.title = `Due ${fmtWhen(dl.due)} — ${fmtLeft(dl.due - Date.now())}`;
    b.appendChild(db);
  }
  if (subTotal(t)) {
    const all = subDone(t) === subTotal(t);
    b.appendChild(el('span', 'badge subs' + (all ? ' is-all' : ''), `✓ ${subDone(t)}/${subTotal(t)}`));
  }
  if (estBaseline(t) !== t.estMin) {
    const rev = el('span', 'badge revised', `est ${fmtDurMin(estBaseline(t))} → ${fmtDurMin(t.estMin)}`);
    rev.title = `Your multiple is measured against the original ${fmtDurMin(estBaseline(t))}.`;
    b.appendChild(rev);
  }
  if (t.done && typeof t.multiple === 'number') {
    b.appendChild(el('span', 'badge mult ' + multClass(t.multiple), t.multiple.toFixed(2) + '×'));
  }
  n.appendChild(b);

  n.appendChild(subtaskBlock(t));

  if (t.done) {
    const meta = el('div', 'timer-row');
    meta.appendChild(el('span', 'est-tag', `${fmtDurMin(t.actualMin)} actual · ${fmtDurMin(estBaseline(t))} estimated`));
    meta.appendChild(el('span', 'spacer'));
    const re = el('button', 'btn btn-ghost', 'Reopen');
    re.addEventListener('click', () => {
      t.done = false; t.multiple = null; t.completedAt = null;
      save(); renderList(); renderStats(); renderStanding(); renderClock();
    });
    meta.appendChild(re);
    n.appendChild(meta);
  } else {
    const row = el('div', 'timer-row');
    const clock = el('span', 'timer-clock', fmtClock(elapsedSec(t)));
    clock.dataset.timer = t.id;
    row.appendChild(clock);
    row.appendChild(el('span', 'est-tag', `/ ${fmtDurMin(t.estMin)}`));
    row.appendChild(el('span', 'spacer'));

    if (t.runSince) {
      const p = el('button', 'btn', '⏸ Pause');
      p.addEventListener('click', () => pauseTask(t.id));
      row.appendChild(p);
    } else {
      const s = el('button', 'btn', (t.accSec ? '▶ Resume' : '▶ Start'));
      s.addEventListener('click', () => startTask(t.id));
      row.appendChild(s);
    }
    const f = el('button', 'btn btn-primary', 'Finish');
    f.addEventListener('click', () => openFinish(t.id));
    row.appendChild(f);
    n.appendChild(row);

    if (pomoOn() && t.runSince && S.pomo.taskId === t.id) {
      const p = el('div', 'pomo-line');
      p.dataset.pomo = t.id;
      const left = pomoWorkLeft();
      p.textContent = left == null ? '' : `🍅 ${fmtClock(left)} left in this pomodoro`;
      n.appendChild(p);
    }

    const bar = el('div', 'bar');
    const i = el('i');
    setBar(i, elapsedSec(t) / 60, t.estMin);
    bar.appendChild(i);
    n.appendChild(bar);
  }
  return n;
}

export function eventRow(ev) {
  const n = el('div', 'item');
  n.style.setProperty('--course', courseColor(ev.courseId));

  const top = el('div', 'item-top');
  top.appendChild(el('div', 'item-title', ev.title));
  const x = el('button', 'item-x', '✕');
  x.title = 'Delete event';
  x.addEventListener('click', () => {
    S.events = S.events.filter((y) => y.id !== ev.id);
    // an event is an hour of the day, so removing one gives an hour back
    touchPlan();
    save(); renderList(); renderStats(); renderScheduled();
    refreshPlanningTabs();
  });
  top.appendChild(x);
  n.appendChild(top);

  const b = el('div', 'badges');
  b.appendChild(el('span', 'badge kind-event', 'event'));
  b.appendChild(el('span', 'badge course', courseName(ev.courseId)));
  b.appendChild(el('span', 'badge sched', '📅 ' + fmtWhen(ev.start)));
  b.appendChild(el('span', 'badge', fmtDurMin((ev.end - ev.start) / 60000)));
  n.appendChild(b);

  if (ev.notes) {
    const p = el('div', 'est-tag', ev.notes);
    p.style.marginTop = '7px';
    n.appendChild(p);
  }

  const row = el('div', 'timer-row');
  row.appendChild(el('span', 'est-tag', 'not timed · excluded from multiple'));
  row.appendChild(el('span', 'spacer'));
  const g = el('button', 'btn', 'Google ↗');
  g.addEventListener('click', () => openInGoogle(ev));
  row.appendChild(g);
  n.appendChild(row);
  return n;
}

export function renderStats() {
  const done = S.tasks.filter((t) => t.done);
  const open = S.tasks.filter((t) => !t.done);
  const m = standingMultiple();
  const loggedMin = done.reduce((a, t) => a + (t.actualMin || 0), 0);
  const plannedMin = open.reduce((a, t) => a + t.estMin, 0);

  $('panelStats').innerHTML = '';
  const add = (v, l, cls) => {
    const s = el('div', 'stat');
    const sv = el('span', 'sv' + (cls ? ' ' + cls : ''), v);
    s.append(sv, el('span', 'sl', l));
    $('panelStats').appendChild(s);
  };
  add(m ? m.toFixed(2) + '×' : '—', 'standing', m ? multClass(m).replace('is-', 'is-') : '');
  add(fmtDurMin(loggedMin), 'logged');
  add(m ? fmtDurMin(plannedMin * m) : fmtDurMin(plannedMin), 'open (projected)');
  // colour the standing stat
  const sv = $('panelStats').querySelector('.sv');
  if (m) sv.style.color = m <= 1.1 ? 'var(--good)' : m <= 1.75 ? 'var(--warn)' : 'var(--bad)';
}

/* ── the day's work ──
   A task belongs to a day by the block it was scheduled into, or by the day it
   was written down when nothing was. Anything still open from an earlier day
   overflows into today rather than falling off the landing page: the day's load
   is everything owed now, not only what was added since midnight.

   "Devoted" is a task's whole banked time, not the slice spent today. The timer
   keeps one running total per task and never cuts it by date, so a task carried
   across three days shows every minute it has cost so far — which is the number
   that matters against the estimate. */

export const dayAnchor = (t) => (typeof t.start === 'number' ? t.start : t.created);
export const devotedMin = (t) => (t.done ? (t.actualMin || 0) : elapsedSec(t) / 60);

export function dayLoad() {
  const today = startOfToday();
  const running = (a, b) => (b.runSince ? 1 : 0) - (a.runSince ? 1 : 0)
    || (a.start || Infinity) - (b.start || Infinity) || a.created - b.created;
  const open = S.tasks.filter((t) => !t.done && dayStart(dayAnchor(t)) <= today);

  return {
    today: open.filter((t) => dayStart(dayAnchor(t)) === today).sort(running),
    overflow: open.filter((t) => dayStart(dayAnchor(t)) < today)
      .sort((a, b) => dayAnchor(a) - dayAnchor(b)),
    finished: S.tasks.filter((t) => t.done && t.completedAt && dayStart(t.completedAt) === today)
      .sort((a, b) => a.completedAt - b.completedAt),
  };
}

/** Every task the day owes, in the order the dock shows them. */
export function dayRows() {
  const { today, overflow, finished } = dayLoad();
  return today.map((t) => ({ t, kind: 'today' }))
    .concat(overflow.map((t) => ({ t, kind: 'overflow' })))
    .concat(finished.map((t) => ({ t, kind: 'finished' })));
}

/** Cheap enough to run on the timer tick: text only, no rebuild. */
export function refreshDayTotal() {
  const out = $('dayDevoted');
  if (!out) return;
  out.textContent = fmtDurMin(dayRows().reduce((a, r) => a + devotedMin(r.t), 0));
}

export function renderDayDock() {
  const dock = $('dayDock');
  const rows = dayRows();
  const devoted = rows.reduce((a, r) => a + devotedMin(r.t), 0);
  const expected = rows.reduce((a, r) => a + r.t.estMin, 0);
  const collapsed = S.ui.dayDock === 'closed';
  const m = standingMultiple();

  dock.textContent = '';
  dock.classList.toggle('is-collapsed', collapsed);
  dock.classList.toggle('is-empty', !rows.length);

  /* ── head: the day, and what it adds up to ── */
  const head = el('div', 'day-head');
  head.appendChild(el('h3', 'day-h', 'Today'));
  head.appendChild(el('span', 'day-date', new Date().toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  })));
  head.appendChild(el('span', 'spacer'));

  if (rows.length) {
    const total = el('div', 'day-total');
    const dev = el('b', 'day-dev', fmtDurMin(devoted));
    dev.id = 'dayDevoted';
    total.append(dev, el('span', 'day-of', ` devoted of ${fmtDurMin(expected)} expected`));
    if (m) {
      const proj = el('em', 'day-proj', `projected ${fmtDurMin(expected * m)} at ${m.toFixed(2)}×`);
      proj.title = 'Your expected time scaled by your standing multiple.';
      total.appendChild(proj);
    }
    head.appendChild(total);

    const bar = el('div', 'bar day-bar');
    const i = el('i');
    setBar(i, devoted, expected);
    bar.appendChild(i);
    head.appendChild(bar);
  }

  const fold = el('button', 'icon-btn day-fold', collapsed ? '▴' : '▾');
  fold.title = collapsed ? 'Show the day' : 'Fold the day away';
  fold.addEventListener('click', () => {
    S.ui.dayDock = collapsed ? 'open' : 'closed';
    save(); renderDayDock();
  });
  head.appendChild(fold);
  dock.appendChild(head);

  if (collapsed) return;

  if (!rows.length) {
    dock.appendChild(el('div', 'day-empty',
      'Nothing on today. Press ＋ New and it lands here — with what you expect it to take.'));
    return;
  }

  const body = el('div', 'day-list');
  let group = null;
  rows.forEach(({ t, kind }) => {
    if (kind !== group) {
      group = kind;
      if (kind === 'overflow') body.appendChild(dayGroupLabel('Carried over', 'Still open from an earlier day.'));
      if (kind === 'finished') body.appendChild(dayGroupLabel('Finished today', 'Logged, and already in your multiple.'));
    }
    body.appendChild(dayRow(t, kind));
  });
  dock.appendChild(body);
}

export function dayGroupLabel(text, hint) {
  const n = el('div', 'day-group', text);
  n.title = hint;
  return n;
}

export function dayRow(t, kind) {
  const n = el('div', 'day-row' + (t.runSince ? ' is-running' : '') + (t.done ? ' is-done' : ''));
  n.dataset.task = t.id;
  n.style.setProperty('--course', courseColor(t.courseId));

  const main = el('div', 'day-main');
  main.appendChild(el('div', 'day-title', t.title));

  const tags = el('div', 'day-tags');
  tags.appendChild(el('span', 'day-tag', courseName(t.courseId)));
  if (kind === 'overflow') {
    const late = Math.round((startOfToday() - dayStart(dayAnchor(t))) / DAY_MS);
    const b = el('span', 'day-tag is-late', late === 1 ? 'from yesterday' : `${late} days old`);
    b.title = `First put down ${fmtWhen(dayAnchor(t))} and still open.`;
    tags.appendChild(b);
  }
  if (t.start) {
    tags.appendChild(el('span', 'day-tag is-time',
      new Date(t.start).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })));
  }
  const dl = deadlineById(t.deadlineId);
  if (dl) {
    const b = el('span', 'day-tag' + (dl.due < Date.now() ? ' is-late' : ''), '⚑ ' + dl.title);
    b.title = `Due ${fmtWhen(dl.due)} — ${fmtLeft(dl.due - Date.now())}`;
    tags.appendChild(b);
  }
  if (subTotal(t)) tags.appendChild(el('span', 'day-tag is-mono', `✓ ${subDone(t)}/${subTotal(t)}`));
  if (t.done && typeof t.multiple === 'number') {
    tags.appendChild(el('span', 'day-tag is-mono ' + multClass(t.multiple), t.multiple.toFixed(2) + '×'));
  }
  main.appendChild(tags);
  n.appendChild(main);

  const meter = el('div', 'day-meter');
  const nums = el('div', 'day-nums');
  const dev = el('b', null, fmtDurMin(devotedMin(t)));
  if (!t.done) dev.dataset.devoted = t.id;      // the shared tick keeps it live
  nums.append(dev, el('span', 'day-vs', ' / '), el('span', 'day-exp', fmtDurMin(t.estMin)));
  meter.appendChild(nums);
  const bar = el('div', 'bar');
  const i = el('i');
  setBar(i, devotedMin(t), t.estMin);
  bar.appendChild(i);
  meter.appendChild(bar);
  n.appendChild(meter);

  const acts = el('div', 'day-acts');
  if (!t.done) {
    const run = !!t.runSince;
    const b = el('button', 'icon-btn', run ? '⏸' : '▶');
    b.title = run ? 'Pause the clock' : (t.accSec ? 'Resume the clock' : 'Start the clock');
    b.addEventListener('click', () => (run ? pauseTask(t.id) : startTask(t.id)));
    acts.appendChild(b);
  }
  const full = el('button', 'icon-btn day-full', '⛶');
  full.title = 'Open as a session — full frame, timer and pomodoro';
  full.addEventListener('click', () => openSession(t.id));
  acts.appendChild(full);
  n.appendChild(acts);

  return n;
}

/* ── the session view ──
   One task, the whole frame, its name across the top. The timer controls and the
   pomodoro settings live only here: a session is the one place work is actually
   being done, so it is the one place those controls mean anything. */

export let sessionId = null;

export function openSession(id) {
  if (!S.tasks.some((t) => t.id === id)) return;
  sessionId = id;
  $('sessionView').hidden = false;
  document.body.classList.add('session-open');
  renderSession();
}

export function closeSession() {
  sessionId = null;
  $('sessionView').hidden = true;
  document.body.classList.remove('session-open');
}

export function renderSession() {
  if (!sessionId) return;
  const t = S.tasks.find((x) => x.id === sessionId);
  if (!t) return closeSession();          // deleted out from under us

  const view = $('sessionView');
  view.dataset.task = t.id;               // so the shared tick finds this bar
  view.style.setProperty('--course', courseColor(t.courseId));
  $('sessName').textContent = t.title;

  const chips = $('sessChips');
  chips.textContent = '';
  chips.appendChild(el('span', 'badge course', courseName(t.courseId)));
  if (t.start) chips.appendChild(el('span', 'badge sched', '📅 ' + fmtWhen(t.start)));
  const dl = deadlineById(t.deadlineId);
  if (dl) {
    const b = el('span', 'badge dl-link' + (dl.due < Date.now() ? ' is-bad' : ''), '⚑ ' + dl.title);
    b.title = `Due ${fmtWhen(dl.due)} — ${fmtLeft(dl.due - Date.now())}`;
    chips.appendChild(b);
  }
  if (t.done && typeof t.multiple === 'number') {
    chips.appendChild(el('span', 'badge mult ' + multClass(t.multiple), t.multiple.toFixed(2) + '×'));
  }

  const sec = elapsedSec(t);
  const clock = $('sessClock');
  clock.textContent = fmtClock(sec);
  clock.dataset.timer = t.id;
  clock.classList.toggle('is-over', sec / 60 > t.estMin);

  const m = standingMultiple();
  $('sessOf').innerHTML = `of <b>${escapeHtml(fmtDurMin(t.estMin))}</b> expected`
    + (m ? ` · <em>projected ${escapeHtml(fmtDurMin(t.estMin * m))} at ${m.toFixed(2)}×</em>` : '');
  setBar($('sessBar'), sec / 60, t.estMin);

  /* the work-phase countdown; the break banner is drawn by renderPomo */
  const line = $('sessPomoLine');
  if (pomoOn() && t.runSince && S.pomo.taskId === t.id) {
    line.dataset.pomo = t.id;
    const left = pomoWorkLeft();
    line.textContent = left == null ? '' : `🍅 ${fmtClock(left)} left in this pomodoro`;
    line.hidden = false;
  } else {
    delete line.dataset.pomo;
    line.textContent = pomoOn()
      ? `🍅 ${S.timer.workMin}-minute focus, ${S.timer.breakMin}-minute break · ${S.pomo.done} done`
      : '';
    line.hidden = !pomoOn();
  }

  const acts = $('sessActions');
  acts.textContent = '';
  if (t.done) {
    acts.appendChild(el('span', 'session-note',
      `logged ${fmtDurMin(t.actualMin)} against ${fmtDurMin(estBaseline(t))} expected`));
    const re = el('button', 'btn btn-big', 'Reopen');
    re.addEventListener('click', () => {
      t.done = false; t.multiple = null; t.completedAt = null;
      save(); renderList(); renderStats(); renderStanding(); renderClock();
    });
    acts.appendChild(re);
  } else {
    const go = el('button', 'btn btn-primary btn-big', t.runSince ? '⏸ Pause' : (t.accSec ? '▶ Resume' : '▶ Start'));
    go.addEventListener('click', () => (t.runSince ? pauseTask(t.id) : startTask(t.id)));
    acts.appendChild(go);

    const fin = el('button', 'btn btn-big', '✓ Finish');
    fin.addEventListener('click', () => openFinish(t.id));
    acts.appendChild(fin);

    const ed = el('button', 'btn btn-ghost btn-big', '✎ Edit');
    ed.addEventListener('click', () => openEdit(t.id));
    acts.appendChild(ed);
  }

  const subs = $('sessSubs');
  subs.textContent = '';
  subs.appendChild(el('h4', 'session-h', 'Steps'));
  subs.appendChild(subtaskBlock(t));

  const meta = $('sessMeta');
  meta.textContent = '';
  const lines = [
    `devoted ${fmtDurMin(devotedMin(t))} · expected ${fmtDurMin(t.estMin)}`,
    `written down ${fmtWhen(t.created)}`,
  ];
  if (estBaseline(t) !== t.estMin) {
    lines.push(`multiple measured against the original ${fmtDurMin(estBaseline(t))}`);
  }
  if (pomoOn()) lines.push('breaks bank no time — only focus counts toward the multiple');
  lines.forEach((l) => meta.appendChild(el('div', 'session-meta-line', l)));
}
